/**
 * Payment Method Service
 * 
 * Handles all payment method-related API calls.
 * 
 * @validates Requirements 1-8 - Payment methods management
 */

import api from './api';
import {
  PaymentMethod,
  PaymentMethodTemplate,
  CreatePaymentMethodRequest,
  UpdatePaymentMethodRequest,
  ResetTemplatesResult,
  ApiResponse,
} from '@budgira/shared';

export const paymentMethodService = {
  /**
   * Get all payment methods for the current user
   */
  async list(): Promise<ApiResponse<PaymentMethod[]>> {
    const response = await api.get<ApiResponse<PaymentMethod[]>>('/payment-methods');
    return response.data;
  },

  /**
   * Get a specific payment method by ID
   */
  async getById(id: string): Promise<ApiResponse<PaymentMethod>> {
    const response = await api.get<ApiResponse<PaymentMethod>>(`/payment-methods/${id}`);
    return response.data;
  },

  /**
   * Create a new custom payment method
   */
  async create(data: CreatePaymentMethodRequest): Promise<ApiResponse<PaymentMethod>> {
    const response = await api.post<ApiResponse<PaymentMethod>>('/payment-methods', data);
    return response.data;
  },

  /**
   * Update a payment method
   */
  async update(id: string, data: UpdatePaymentMethodRequest): Promise<ApiResponse<PaymentMethod>> {
    const response = await api.put<ApiResponse<PaymentMethod>>(`/payment-methods/${id}`, data);
    return response.data;
  },

  /**
   * Delete a payment method
   */
  async delete(id: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/payment-methods/${id}`);
    return response.data;
  },

  /**
   * Get templates available for re-addition
   * @validates Requirement 6.1, 6.2
   */
  async getAvailableTemplates(): Promise<ApiResponse<PaymentMethodTemplate[]>> {
    const response = await api.get<ApiResponse<PaymentMethodTemplate[]>>('/payment-methods/templates/available');
    return response.data;
  },

  /**
   * Add a template to the user's collection
   * @validates Requirement 6.4
   */
  async addTemplate(templateId: string): Promise<ApiResponse<PaymentMethod>> {
    const response = await api.post<ApiResponse<PaymentMethod>>(`/payment-methods/templates/${templateId}/add`);
    return response.data;
  },

  /**
   * Reset templates (add all missing templates)
   * @validates Requirement 7.2, 7.3, 7.6
   */
  async resetTemplates(): Promise<ApiResponse<ResetTemplatesResult>> {
    const response = await api.post<ApiResponse<ResetTemplatesResult>>('/payment-methods/templates/reset');
    return response.data;
  },

  /**
   * Remove all template-based payment methods (keeps personal ones)
   */
  async removeTemplate(): Promise<ApiResponse<{ count: number }>> {
    const response = await api.post<ApiResponse<{ count: number }>>('/payment-methods/templates/remove');
    return response.data;
  },

  /**
   * Purge unused personal payment methods (not used in operations/schedulers)
   */
  async purgeUnused(): Promise<ApiResponse<{ count: number }>> {
    const response = await api.post<ApiResponse<{ count: number }>>('/payment-methods/purge-unused');
    return response.data;
  },

  /**
   * Update payment method logo
   */
  async updateLogo(id: string, logoUrl: string): Promise<ApiResponse<PaymentMethod>> {
    const response = await api.post<ApiResponse<PaymentMethod>>(`/payment-methods/${id}/logo`, { logoUrl });
    return response.data;
  },
};

export default paymentMethodService;
